import compact from 'lodash/compact'
import find from 'lodash/find'
import map from 'lodash/map'
import startCase from 'lodash/startCase'
import React, { useContext } from 'react'

import { generateProductLinkingDataImage } from '@Components/Image'
import { ProductContext } from '@Components/Product/Context'
import { BaseAttribute } from '@Generated/graphql'
import { useLocale } from '@Globals/Locale'

function arrayOrSingleOrUndefined<T>(array: T[] | undefined | null): T | T[] | undefined {
  if (!array || !Array.isArray(array)) {
    return undefined
  }

  if (array.length === 0) {
    return undefined
  }

  if (array.length === 1) {
    return array[0]
  }

  return array
}

export const ProductLinkedData = () => {
  const locale = useLocale()
  const product = useContext(ProductContext)

  if (!product) {
    return null
  }

  const images = new Set<string>()
  // @ts-ignore
  if (product?.primaryAsset?.url) {
    // @ts-ignore
    images.add(generateProductLinkingDataImage(product.primaryAsset.url))
  }

  product?.assets?.forEach((asset) => {
    if (!asset.url || !asset.tags || !asset.tags.includes('gallery') || asset.tags.includes('video')) {
      return
    }

    images.add(generateProductLinkingDataImage(asset.url))
  })

  const imageURLs = Array.from(images)

  const reviews = compact(map(product?.productReviews?.productReviews, (review) => {
    if (!locale || review.locale !== locale) {
      return null
    }

    return {
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.customerName,
      },
      datePublished: review.createdAt,
      name: review.localizedTitle,
      reviewBody: review.localizedText,
      reviewRating: {
        '@type': 'Rating',
        bestRating: 5,
        ratingValue: review.rating,
        worstRating: 1,
      },
    }
  }))

  let productDescription = product?.description
  const descriptionGenerated = (
    (find(product?.masterVariant?.attributes, ['key', 'descriptionGenerated']) as BaseAttribute)?.value || undefined
  ) as Record<string, string> | undefined

  if (descriptionGenerated?.[locale]) {
    try {
      const parsed = JSON.parse(descriptionGenerated[locale])
      if (typeof parsed === 'object' && 'text' in parsed && parsed.text) {
        productDescription = parsed.text
      }
    } catch {
      // ignore errors
    }
  }

  return (
    <script
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'http://schema.org',
          '@type': 'Product',
          name: product?.name,
          brand: product?.brand?.title
            ? {
              '@type': 'Brand',
              name: product?.brand?.title,
            }
            : undefined,
          description: productDescription,
          url: product.url,
          category: product?.type ? startCase(product.type) : undefined,
          image: arrayOrSingleOrUndefined(imageURLs),
          color: arrayOrSingleOrUndefined((product?.masterVariant as any)?.linkingDataColors?.value),
          material: arrayOrSingleOrUndefined((product?.masterVariant as any)?.linkingDataMaterials?.value),
          offers: product?.masterVariant
            ? {
              '@type': 'Offer',
              availability: product.availability?.isInStock ? 'http://schema.org/InStock' : 'http://schema.org/OutOfStock',
              itemCondition: 'http://schema.org/NewCondition',
              price: (product.masterVariant as any).linkingDataPrice?.discount?.format || (product.masterVariant as any)?.linkingDataPrice?.format,
              priceCurrency: (product.masterVariant as any).linkingDataPrice?.currencyCode,
            }
            : undefined,
          productID: product.id,
          ...product?.productReviews?.count && {
            aggregateRating: {
              '@type': 'AggregateRating',
              bestRating: 5,
              ratingValue: product?.productReviews?.averageRating,
              reviewCount: product?.productReviews?.count,
              worstRating: 1,
            },
          },
          review: arrayOrSingleOrUndefined(reviews),
        }),
      }}
      id="product-ld"
      type="application/ld+json"
    />
  )
}
